import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };

const hexToRgba = (hex, a) => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return `rgba(${r},${g},${b},${a})`; };

function KPI({ label, value, iconName, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: hexToRgba(color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={22} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{label}</div>
        <div style={{ fontSize: '26px', fontWeight: '900', color: '#111827', lineHeight: 1 }}>{value}</div>
      </div>
    </div>
  );
}

export default function DashPartnerActivity() {
  const token = localStorage.getItem('doorly_admin_token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/dashboard/partner-activity-data`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <AdminLayout><div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#9ca3af', fontSize: '14px' }}>Loading...</div></AdminLayout>;

  const p = data?.partners || {};
  const ref = data?.referrals || {};
  const top = data?.top_partners || [];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Partner Activity</h1>
          <p style={{ fontSize: '13px', color: '#6b7280' }}>Referral health and partner performance</p>
        </div>

        {/* 🚨 TWO ROWS, BECAUSE SIX CARDS IN ONE STRIP HID WHICH POPULATION EACH
            NUMBER WAS ABOUT. "Pending" sat between "Referrals MTD" and
            "Registered" and counted PENDING REFERRALS, while the partner cards
            two places to its left had their own pending figure that was never
            shown. A reader checking Total Partners against Active had no way to
            see what the difference consisted of.

            🔑 AND THE PARTNER ROW NOW ADDS UP ON SIGHT — Total = Active +
            Pending + Invited, the same four cards and the same arithmetic as the
            Partners tab, from the same predicate. See utils/partnerKpis.js. */}
        <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Partners</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '22px' }}>
          <KPI label="Total Partners" value={p.total || 0} iconName="users" color="#3a6ad9" />
          <KPI label="Active" value={p.active || 0} iconName="check-circle" color="#acf44e" />
          <KPI label="Pending Onboarding" value={p.pending || 0} iconName="clock" color="#7a5200" />
          <KPI label="Invited" value={p.invited || 0} iconName="mail" color="#7a5200" />
        </div>

        <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Referrals</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <KPI label="Total Referrals" value={ref.total || 0} iconName="arrow" color="#3a6ad9" />
          <KPI label="Registered" value={ref.registered || 0} iconName="user" color="#acf44e" />
          {/* ⚠️ LABELLED "Awaiting Registration", NOT "Pending". The number is
              referrals with no borrower account yet, and a bare "Pending" on a
              page that also counts partners pending onboarding reads as either. */}
          <KPI label="Awaiting Registration" value={ref.pending || 0} iconName="clock" color="#ff082f" />
          <KPI label="Referrals MTD" value={ref.mtd || 0} iconName="chart" color="#ffdd86" />
        </div>

        <div className="admin-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          {/* Partners by Type */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Partners by Type</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(p.by_type || []).map(t => (
                <div key={t.type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#374151', textTransform: 'capitalize' }}>{(t.type || 'unknown').replace(/_/g, ' ')}</span>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#9ca3af' }}>{t.active_count || 0} active</span>
                    <span style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>{t.count}</span>
                  </div>
                </div>
              ))}
              {(!p.by_type || p.by_type.length === 0) && (
                <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' }}>No partners yet</div>
              )}
            </div>
          </div>

          {/* Referral Stats */}
          <div style={card}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Referral Breakdown</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { label: 'Total Referrals', value: ref.total || 0, color: '#3a6ad9' },
                { label: 'Registered', value: ref.registered || 0, color: '#acf44e' },
                { label: 'Awaiting Registration', value: ref.pending || 0, color: '#ff082f' },
                { label: 'Referrals MTD', value: ref.mtd || 0, color: '#3a6ad9' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#374151' }}>{r.label}</span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: r.color }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Partners Table */}
        <div style={card}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Top Partners</h3>
          {top.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '13px' }}>No partner activity yet</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  {/* 🚨 "Closed" USED TO MEAN "Registered". The column was
                      COUNT(*) FILTER (WHERE status='closed' OR status='registered')
                      over buyer_referrals, whose status is only ever pending,
                      registered or archived — so the 'closed' half matched nothing
                      and the column reported account sign-ups under a heading that
                      says the loan funded. Both facts are worth showing; each now
                      has its own column and its own name. */}
                  {['Partner', 'Type', 'Referrals', 'Registered', 'Closed'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top.map((t, i) => (
                  <tr key={t.email || i} style={{ borderBottom: i < top.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '500', color: '#111827' }}>{t.name}</td>
                    <td style={{ padding: '10px 12px', color: '#374151', textTransform: 'capitalize' }}>{(t.type || '').replace(/_/g, ' ')}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{t.referrals}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{t.registered}</td>
                    {/* ⚠️ A ZERO IS RENDERED AS A ZERO, not dimmed away. No loan has
                        closed yet, and a column of honest zeros is the answer — it
                        is what made the old number look wrong in the first place. */}
                    <td style={{ padding: '10px 12px', fontWeight: '600', color: '#111827' }}>{t.closed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
